import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import createStyle from 'shared-utils/src/createStyle';
import makeClassName from 'shared-utils/src/makeClassName';
import LockScreen from 'shared-utils/src/lockScreen';
import trackGTMEvents from 'shared-utils/src/trackGTMEvents/new';

import casaClaim from 'shared-constants/src/header_footer/casaClaim';
import navigationLinks from 'shared-constants/src/header_footer/navigationLinks';
import socialLinks from 'shared-constants/src/header_footer/socialLinks';

import CloseIcon from 'shared-vectors/icons/CloseL';
import Stared from 'shared-vectors/icons/Stared';
import Icon from 'shared-vectors/src/components/Icon';

import Grid from '../Grid';
import GridItem from '../GridItem';

import styles, { mainCls } from './style';

const useStyles = createStyle(styles);

const propTypes = {
  toggleNav: PropTypes.func,
  isOpen: PropTypes.bool,
  isTransparent: PropTypes.bool,
  isFixed: PropTypes.bool,
  isHidden: PropTypes.bool,
  portalDomain: PropTypes.string.isRequired,
  mobileOs: PropTypes.string,
  fromPortal: PropTypes.bool,
};
const defaultProps = {
  toggleNav: () => {},
  isOpen: false,
  isTransparent: false,
  isFixed: false,
  isHidden: false,
  mobileOs: '',
  fromPortal: false,
};

const Navigation = ({
  toggleNav,
  isOpen,
  isTransparent,
  isFixed,
  isHidden,
  portalDomain,
  mobileOs,
  fromPortal,
}) => {
  const [lockScreen, setLockScreen] = useState(null);
  useEffect(
    () => {
      if (mobileOs) {
        setLockScreen(new LockScreen('nav-locked'));
      }
    }, [mobileOs],
  );

  /** una volta caricate le immagini non c'è bisogno spariscano alla chiusura */
  const [keepImgs, setKeepImages] = useState(false);
  useEffect(
    () => {
      if (isOpen) {
        if (lockScreen) {
          lockScreen.lock();
        }
        if (!keepImgs) {
          setKeepImages(true);
        }
      } else {
        if (lockScreen) {
          lockScreen.unLock();
        }
      }
    }, [isOpen],
  );
  /** una volta caricate le immagini non c'è bisogno spariscano alla chiusura */

  const cls = useStyles();
  const navCls = makeClassName([
    cls[mainCls],
    'pl--m',
    isOpen && cls[`${mainCls}--opn`],
    keepImgs && cls[`${mainCls}--wImgs`],
    isFixed && cls[`${mainCls}--fix`],
    isHidden && cls[`${mainCls}--hid`],
  ]);
  const ovrlCls = makeClassName([
    cls[`${mainCls}__ovrl`],
    isOpen && cls[`${mainCls}__ovrl--opn`],
  ]);

  const trackAppClick = () => trackGTMEvents({
    category: 'MigrationToApp',
    action: 'ContinueInApp',
    label:'Menu',
  });

  const appBoost = mobileOs
    ? (
      <div className={cls[`${mainCls}__bapp`]}>
        <Grid>
          <GridItem
            behavior="fixed"
            className={makeClassName([
              cls[`${mainCls}__bapp-lg`],
              'c-bg--w',
            ])}
          />
          <GridItem>
            <p className={`${cls[`${mainCls}__bapp-p`]} tp-s--l tp-w--l c-txt--w`}>Scarica l’App. È semplice.</p>
            <div className={cls[`${mainCls}__bapp-stars`]}>
              {[1, 2, 3, 4].map(i => <Icon id={`star_${i}`} name={Stared} className="mr--xs" color="f1" size="m" />)}
              <div className={cls[`${mainCls}__bapp-star`]} />
            </div>
          </GridItem>
        </Grid>
        {
          mobileOs === 'ios'
            ? (
              <a
                className={`${cls[`${mainCls}__bapp-ios`]} mt--m`}
                href="https://apps.apple.com/app/apple-store/id470456810?pt=118565387&ct=web-casa_downloadApp&mt=8"
                title="Scarica l'app di Casa.it dall'App Store"
                onClick={trackAppClick}
                rel="nofollow"
              >
                Scarica l'app di Casa.it dall'App Store
              </a>
            ) : (
              <a
                className={`${cls[`${mainCls}__bapp-and`]} mr--m`}
                href="https://play.google.com/store/apps/details?id=it.casa.app&utm_source=web-casa&utm_campaign=downloadApp&utm_medium=direct"
                title="Scarica l'app di Casa.it da Google Play"
                onClick={trackAppClick}
                rel="nofollow"
              >
                Scarica l'app di Casa.it da Google Play
              </a>             
            )
        }
      </div>
    ) : null;

  const baseUrl = fromPortal
    ? ''
    : `${portalDomain}`;

  return (
    <>
      <nav className={navCls}>
        {/* <div className={`${cls[`${mainCls}__head`]} mt--m pr--m`}> */}
          <button
            type="button"
            className={`${cls[`${mainCls}__head--cls`]} tp-s--s tp-w--m c-txt--w`}
            onClick={toggleNav}
          >
            <Icon name={CloseIcon} width="18px" height="18px" />
          </button>
        {/* </div> */}
        <div className={`${cls[`${mainCls}__scrl`]} pr--m`}>
          <div className={cls[`${mainCls}__scrl-ctn`]}>
            <ul className={cls[`${mainCls}__ul`]}>
              {
                navigationLinks.map(
                  (link) => {
                    const liCls = makeClassName([
                      cls[`${mainCls}__li`],
                      link.mobile && !link.desktop && cls[`${mainCls}__li--mob`],
                      !link.mobile && link.desktop && cls[`${mainCls}__li--dsk`],
                      link.isNew && cls[`${mainCls}__li--new`],
                      link.isFirstDsk && cls[`${mainCls}__li--nomar`],
                    ]);
                    const lnkCls = makeClassName([
                      cls[`${mainCls}__lnk`],
                      link.isBold ? 'tp-w--l' : 'tp-w--m',
                      isTransparent && cls[`${mainCls}__lnk--tras`],
                    ]);
                    
                    const action = () => {
                      if (link.url === '/pubblica-annunci') {
                        return trackGTMEvents({
                          category: 'Interaction',
                          action: 'GoToInsertAdsPage',
                          label: 'MenuTextButton',
                        }, null, true);
                      }
                      if (link.url === '/trova-agenzia/') {
                        return trackGTMEvents({
                          category: 'AgencyRecommender',
                          action: 'GoToAgencyRecommender',
                          position: 'HeaderMenu',
                        });
                      }
                      if (link.url === '/valutazione-immobile/') {
                        return trackGTMEvents({
                          category: 'Valuation',
                          action: 'GoToPropertyValuation',
                          position: 'HeaderMenu',
                        });
                      }
                      return null;
                    };
                    
                    return (
                      <li key={`nav_${link.label}`} className={liCls}>
                        <a
                          href={link.url.indexOf('http') < 0 ? `${baseUrl}${link.url}` : link.url}
                          title={link.altText}
                          className={lnkCls}
                          onClick={action}
                        >
                          {link.label}
                        </a>
                      </li>
                    );
                  }
                )
              }
            </ul>
            {appBoost}
            <div className={cls[`${mainCls}__share`]}>
              {
                socialLinks.map(
                  (link) => (
                    <a
                      key={`${link.url}__socialLinks`}
                      href={link.url}
                      title={link.altText}
                      className={link.class}
                      rel={link.rel}
                      target={link.target}
                    >
                      {link.altText}
                    </a>
                  )
                )
              }
            </div>
            <a href={fromPortal ? '/' : portalDomain} title={casaClaim} className={cls[`${mainCls}__logo`]}>
              {casaClaim}
            </a>
          </div>
        </div>
      </nav>
      <div className={ovrlCls} onClick={toggleNav} />
    </>
  );
};
Navigation.propTypes = propTypes;
Navigation.defaultProps = defaultProps;
export default Navigation;
